import React, { useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import blogPosts from '../data/blogPosts';

import { SITE_URL } from '../components/GlobalSchema';
const SITE = SITE_URL;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  // `blogPosts` is a module-level import; `p` is closure-local.
  // Only `post` is a real reactive dep.
  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter((p) => p.slug !== post.slug && p.category === post.category)
      .slice(0, 3);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `${SITE}/blog/${post.slug}`;
  // Author slug: fall back to a single team page since the spec requires /team/<slug> linking
  const authorSlug = post.authorSlug || 'abl-finance-team';

  const breadcrumbsSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url }
    ]
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: [`${SITE}/logo.png`],
    datePublished: post.date,
    dateModified: post.dateModified || post.date,
    author: {
      '@type': 'Person',
      name: post.author,
      url: `${SITE}/team/${authorSlug}`
    },
    publisher: { '@id': `${SITE}/#organization` },
    mainEntityOfPage: url,
    articleSection: post.category,
    inLanguage: 'en-GB'
  };

  const faqSchema =
    post.faqs && post.faqs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        }
      : null;

  return (
    <div className="bg-white">
      <Helmet>
        <title>{`${post.title} | ABL Finance Blog`}</title>
        <meta name="description" content={post.excerpt.length > 160 ? `${post.excerpt.slice(0, 157)}...` : post.excerpt} />
        <meta name="keywords" content={`${post.category}, SBLC, bank guarantee, trade finance, ABL Finance`} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${post.title} | ABL Finance Blog`} />
        <meta property="og:description" content={post.excerpt.length > 160 ? `${post.excerpt.slice(0, 157)}...` : post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ABL Finance" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE}/logo.png`} />
        <meta property="og:locale" content="en_GB" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.dateModified || post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | ABL Finance Blog`} />
        <meta name="twitter:description" content={post.excerpt.length > 160 ? `${post.excerpt.slice(0, 157)}...` : post.excerpt} />
        <meta name="twitter:image" content={`${SITE}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbsSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-emerald-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-emerald-300 hover:text-emerald-200 mb-6 transition-colors text-sm"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All articles
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-emerald-200">
            <span className="px-3 py-1 rounded-full bg-emerald-600/30 border border-emerald-500/40 font-semibold">
              {post.category}
            </span>
            <span className="inline-flex items-center">
              <Calendar className="w-3.5 h-3.5 mr-1" />
              Published {formatDate(post.date)}
            </span>
            {post.dateModified && post.dateModified !== post.date && (
              <span className="inline-flex items-center">
                Updated {formatDate(post.dateModified)}
              </span>
            )}
            <span className="inline-flex items-center">
              <Clock className="w-3.5 h-3.5 mr-1" />
              {post.readTime}
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-bold leading-tight mb-6"
            data-testid="blog-post-h1"
          >
            {post.title}
          </h1>
          <p className="text-lg md:text-xl text-gray-200 leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 flex items-center text-sm text-emerald-200">
            <User className="w-4 h-4 mr-2" />
            By&nbsp;
            <Link
              to={`/team/${authorSlug}`}
              className="underline underline-offset-2 hover:text-white"
              data-testid="blog-author-link"
            >
              {post.author}
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {post.content.map((section) => (
          <section key={section.heading} className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              {section.heading}
            </h2>
            <p className="text-slate-700 leading-relaxed text-lg">{section.text}</p>
          </section>
        ))}

        {/* Internal links */}
        <section className="mb-10 p-6 bg-slate-50 rounded-xl border border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Related ABL Finance services</h2>
          <ul className="space-y-2 text-slate-700">
            <li>
              <Link
                to="/services/sblc"
                className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline"
              >
                Standby Letter of Credit (SBLC) services
              </Link>
              {' '}— Issuance, monetization and advisory.
            </li>
            <li>
              <Link
                to="/services/bank-guarantee"
                className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline"
              >
                Bank Guarantee services
              </Link>
              {' '}— Bid bonds, performance and advance payment guarantees.
            </li>
            <li>
              <Link
                to="/services/trade-finance"
                className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline"
              >
                Trade Finance solutions
              </Link>
              {' '}— LCs, documentary collections and supply chain finance.
            </li>
            <li>
              <Link
                to="/contact"
                className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline"
              >
                Contact our team
              </Link>
              {' '}— Free consultation within 24 hours.
            </li>
          </ul>
        </section>

        {/* FAQ */}
        {post.faqs && post.faqs.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-testid="blog-faq-list">
              {post.faqs.map((faq) => (
                <Card key={faq.q}>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                    <p className="text-slate-700 leading-relaxed">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <p className="text-sm text-slate-500 mt-12">
          Last updated: {formatDate(post.dateModified || post.date)}
        </p>
      </article>

      {/* Related */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-slate-50 border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
              More from {post.category}
            </h2>
            <div className="grid md:grid-cols-3 gap-6" data-testid="related-posts-grid">
              {relatedPosts.map((rp) => (
                <Link
                  key={rp.slug}
                  to={`/blog/${rp.slug}`}
                  className="group"
                  data-testid={`related-card-${rp.slug}`}
                >
                  <Card className="h-full hover:shadow-xl transition-shadow border-slate-200 hover:border-emerald-300">
                    <CardContent className="pt-6">
                      <div className="text-xs text-slate-500 mb-2 inline-flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(rp.date)}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">
                        {rp.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">{rp.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to discuss your transaction?</h2>
          <p className="text-lg text-emerald-100 mb-8 leading-relaxed">
            Speak to an ABL Finance specialist for tailored advice on {post.category.toLowerCase()}.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-emerald-700 hover:bg-gray-100"
                data-testid="post-cta-contact"
              >
                Request a Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="tel:+442032914022">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-emerald-700"
                data-testid="post-cta-phone"
              >
                <Phone className="mr-2 w-4 h-4" /> +44 203 291 4022
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
